import { FC } from '../../../Services'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { showToast } from '../../../utils/toast'
import airports from '../Resources/airports.json'

const language = cap.language.state || 'IT'
const t = translations[language]

export const readKey = (json, location, count) => {
  // funzione per leggere una chiave di un json
  const chiavi = location.split('.') // splitto la location per ottenere le chiavi
  let valore = json // inizializzo il valore con il json
  for (const chiave of chiavi) valore = valore?.[chiave === '#COUNT' ? count : chiave] // per ogni chiave, se è #COUNT prendo il count, altrimenti prendo la chiave
  return valore // ritorno il valore
}

export const writeKey = (json, location, count, valore) => {
  // funzione per scrivere una chiave di un json
  const chiavi = location.split('.') // splitto la location per ottenere le chiavi
  let puntatore = json // inizializzo il puntatore con il json
  for (let i = 0; i < chiavi.length - 1; i++)
    puntatore = puntatore?.[chiavi[i] === '#COUNT' ? count : chiavi[i]] // per ogni chiave, se è #COUNT prendo il count, altrimenti prendo la chiave
  if (puntatore) {
    puntatore[chiavi[chiavi.length - 1]] = valore // scrivo il valore
    return json
  }
  return {}
}

export const getServiceCount = (json, service) =>
  json?.transferInfo?.infoPax?.listOfPax?.[0]?.infoServices?.[service]?.length || 0

const SERVICE_CARD_LABELS = {
  train: t.treno.toUpperCase(),
  plane: t.aereo.toUpperCase(),
  hotel: t.hotel.toUpperCase(),
  car: t.auto.toUpperCase()
}

const shortenPlaceLabel = (value) => {
  if (!value) return ''
  const text = String(value).trim()
  const dashPart = text.split(' - ')[0]
  return (dashPart || text).split(',')[0].trim().toUpperCase()
}

/**
 * Costruisce il titolo della card servizio (es. "TRENO 1 - ROMA -> MILANO")
 * @param {Object} state - Stato del form Gigi
 * @param {string} serviceType - Tipo servizio (train, plane, hotel, car)
 * @param {number} index - Indice del servizio nella lista
 * @returns {string} Titolo formattato per l'intestazione della card
 */
export const getServiceCardTitle = (state, serviceType, index) => {
  const typeLabel = SERVICE_CARD_LABELS[serviceType] || String(serviceType || '').toUpperCase()
  const num = index + 1
  const base = `transferInfo.infoPax.listOfPax.0.infoServices.${serviceType}.#COUNT.`
  let from = ''
  let to = ''

  switch (serviceType) {
    case 'train':
      from = readKey(state, `${base}trainCityDeparture`, index)
      to = readKey(state, `${base}trainCityArrival`, index)
      break
    case 'plane':
      from =
        readKey(state, `${base}airportDepartureDescription`, index) ||
        readKey(state, `${base}airportDeparture`, index)
      to =
        readKey(state, `${base}airportArrivalDescription`, index) ||
        readKey(state, `${base}airportArrival`, index)
      break
    case 'hotel':
      from = readKey(state, `${base}hotelName`, index) || readKey(state, `${base}hotelCity`, index)
      break
    case 'car':
      from = readKey(state, `${base}pickupPlace`, index)
      to = readKey(state, `${base}dropoffPlace`, index)
      break
    default:
      break
  }

  from = shortenPlaceLabel(from)
  to = shortenPlaceLabel(to)

  if (from && to) return `${typeLabel} ${num} - ${from} -> ${to}`
  if (from) return `${typeLabel} ${num} - ${from}`
  return `${typeLabel} ${num}`
}

const getAirportFromOptions = (city, options) => {
  const [filteredDepartureAirport] = [
    ...options.filter(
      ({ label }) =>
        label?.includes('TUTTI AEROPORTI') && label.toLowerCase().includes(city.toLowerCase())
    ),
    ...options.filter(({ label }) => label.toLowerCase().includes(city.toLowerCase()))
  ]
  return {
    value: filteredDepartureAirport?.value || '',
    label: filteredDepartureAirport?.label || ''
  }
}

export const addService = async (json, service) => {
  const newJson = JSON.parse(JSON.stringify(json))
  const necessaryFields = serviceFields.filter((field) => field.service === service)
  let jsonToPush = {}
  for (const field of necessaryFields) jsonToPush[field.id] = ''
  const {
    transfer: { departureDate, arrivalDate, departurePlace, arrivalPlace },
    plane: { airportDeparture, airportArrival }
  } = getElementsByService()

  // precarico i campi necessari per ogni nuovo servizio
  if (service === 'hotel') {
    jsonToPush.checkInDate = readKey(json, departureDate.location)
    jsonToPush.checkOutDate = readKey(json, arrivalDate.location)
    jsonToPush.hotelCity = readKey(json, arrivalPlace.location)
  }
  if (service === 'car') {
    jsonToPush.pickupDate = readKey(json, departureDate.location)
    jsonToPush.dropoffDate = readKey(json, arrivalDate.location)
    jsonToPush.pickupPlace = readKey(json, arrivalPlace.location)
    jsonToPush.dropoffPlace = readKey(json, arrivalPlace.location)
    jsonToPush.carCategory = '_'
    jsonToPush.carType = '_'
    jsonToPush.carTransmission = '_'
  }
  if (service === 'train') {
    jsonToPush.typeTrip = 'OW'
    jsonToPush.departureDate = readKey(json, departureDate.location)
    jsonToPush.arrivalDate = readKey(json, arrivalDate.location)
    jsonToPush.trainCityDeparture = readKey(json, departurePlace.location)?.split(',')[0]
    jsonToPush.trainCityArrival = readKey(json, arrivalPlace.location)?.split(',')[0]
    jsonToPush.carnet = []
    if (!json?.environmentInfo?.skipTrainStationSearch) {
      if (jsonToPush.trainCityDeparture) {
        const [res] = await FC.service('info').get('getTrainStation', {
          query: { inputTrain: jsonToPush.trainCityDeparture }
        })
        if (res) jsonToPush.trainStationDepartureDyn = res
      }
      if (jsonToPush.trainCityArrival) {
        const [res2] = await FC.service('info').get('getTrainStation', {
          query: { inputTrain: jsonToPush.trainCityArrival }
        })
        if (res2) jsonToPush.trainStationArrivalDyn = res2
      }
    }
  }
  if (service === 'plane') {
    jsonToPush.typeTrip = 'OW'
    jsonToPush.departureDate = readKey(json, departureDate.location)
    jsonToPush.arrivalDate = readKey(json, arrivalDate.location)
    const departureCity = readKey(json, departurePlace.location)?.split(',')[0]
    if (departureCity) {
      const { value, label } = getAirportFromOptions(departureCity, airportDeparture.options)
      jsonToPush = { ...jsonToPush, airportDeparture: value, airportDepartureDescription: label }
    }
    const arrivalCity = readKey(json, arrivalPlace.location)?.split(',')[0]
    if (arrivalCity) {
      const { value: arrivalValue, label: arrivalLabel } = getAirportFromOptions(
        arrivalCity,
        airportArrival.options
      )
      jsonToPush = {
        ...jsonToPush,
        airportArrival: arrivalValue,
        airportArrivalDescription: arrivalLabel
      }
    }
  }
  if (!newJson?.transferInfo?.infoPax?.listOfPax[0]?.infoServices?.[service]?.length) {
    newJson.transferInfo = {
      ...newJson?.transferInfo,
      infoPax: {
        ...newJson.transferInfo?.infoPax,
        listOfPax: [
          {
            ...newJson.transferInfo?.infoPax?.listOfPax[0],
            infoServices: {
              ...newJson?.transferInfo?.infoPax?.listOfPax[0]?.infoServices,
              [service]: []
            }
          }
        ]
      }
    }
  }
  newJson.transferInfo.infoPax.listOfPax[0].infoServices[service].push(jsonToPush)
  return newJson
}

export const removeService = (json, service, index) => {
  const newJson = JSON.parse(JSON.stringify(json))
  newJson.transferInfo.infoPax.listOfPax[0].infoServices[service].splice(index, 1)
  return newJson
}

export const getCorrectState = (state) => {
  state?.transferInfo?.infoPax?.listOfPax?.forEach((pax) => {
    const services = Object.keys(pax.infoServices)
    pax.requestedServices = {}
    for (const service of services) {
      pax.requestedServices[service] = pax.infoServices[service]?.length > 0
      if (!pax.requestedServices[service]) delete pax.requestedServices[service]
    }
  })
  const {
    pid,
    unit,
    user,
    codSociety,
    tomcatCode,
    travellerId,
    languageInfo,
    transferInfo,
    environmentId,
    userEmail,
    referenceWhatsapp,
    referencesBtmEmail,
    moduleTag,
    moduleEnv,
    errorProcessToLoad,
    operatorEmail,
    analisiStorico,
    levelFormalita,
    environmentInfo,
    conversationData,
    profileDataMittente,
    communicationChannel,
    googleCoordinates,
    communityPreset,
    modificationCancellationData,
    typeOfMessage,
    draftMessage,
    mailInfoData,
    mailReferencesEmail,
    processLoadQuotes,
    unitCallConversationExist,
    solutionSended,
    frontConfig,
    selfManagedDataCollection,
    dataQuestionUnit,
    escludeMidOfficeFlow,
    sendEmail
  } = state
  return {
    moduleTag,
    moduleEnv,
    pid,
    unit,
    userEmail,
    user,
    codSociety,
    tomcatCode,
    travellerId,
    languageInfo,
    transferInfo,
    environmentId,
    operatorEmail,
    analisiStorico,
    levelFormalita,
    environmentInfo,
    conversationData: {
      conversationId: conversationData?.conversationId
    },
    profileDataMittente,
    referenceWhatsapp,
    referencesBtmEmail,
    mailReferencesEmail,
    processLoadQuotes,
    unitCallConversationExist,
    errorProcessToLoad,
    communicationChannel,
    googleCoordinates,
    communityPreset,
    modificationCancellationData,
    typeOfMessage,
    draftMessage,
    mailInfoData,
    solutionSended,
    frontConfig,
    // Parametri del client Goelett: senza, la ripartenza dal form cade sul gate MidOffice e
    // manda l'email anche dove il preventivo era nato con l'invio spento.
    selfManagedDataCollection,
    dataQuestionUnit,
    escludeMidOfficeFlow,
    sendEmail
  }
}

/**
 * Crea un nuovo processo asincrono basato sullo stato corrente, un identificativo di conversazione e il nome dell'unità.
 *
 * Questa funzione asincrona tenta di creare un nuovo processo invocando un servizio esterno.
 * Utilizza i parametri forniti per costruire la richiesta al servizio 'info' con azione 'createNewProcess'.
 * In caso di successo, estrae l'ID del processo creato dalla risposta e lo restituisce.
 * Se si verifica un errore durante la creazione del processo, viene mostrato un messaggio di errore
 * tramite una notifica 'growl' e viene restituito 'null'.
 *
 * @param state Oggetto che rappresenta lo stato attuale utilizzato nella creazione del processo.
 * @param conversationId Identificativo della conversazione associata al nuovo processo.
 * @param unitName Nome dell'unità associata al processo da creare.
 * @return {Promise<string|null>} Promessa che risolve nell'ID del nuovo processo creato o 'null' in caso di errore.
 */
export const createNewProcess = async (state, conversationId, unitName) => {
  try {
    const { id: processId } = await FC.service('info').create({
      action: 'createNewProcess',
      state,
      conversationId,
      unitName
    })
    return processId
  } catch (e) {
    showToast('error', 'Errore', e?.message || 'Errore conferma form')
    return null
  }
}

/**
 * Ottiene i dati di default per uno stato, con informazioni aggiuntive sui trasferimenti.
 *
 * Questa funzione prende come parametro uno stato e restituisce un nuovo oggetto stato.
 * All'interno dello stato, si assicura che la sezione 'transferInfo' sia aggiornata:
 * - Mantiene tutte le proprietà esistenti dello stato e di 'transferInfo'.
 * - Aggiorna 'infoPax' all'interno di 'transferInfo', includendo una lista di passeggeri 'listOfPax'.
 * - Per ogni passeggero nella lista, mantiene tutti i campi esistenti e aggiunge o mantiene due oggetti:
 *   'campi1a10Found'.
 * - Se la lista dei passeggeri 'listOfPax' è undefined, la inizializza come un array vuoto.
 *
 * @param state Oggetto che rappresenta lo stato attuale da cui ottenere i dati di default.
 * @return Un nuovo oggetto stato con i dati di default aggiornati per i trasferimenti.
 */
export const getStateDefaultData = (state) => ({
  ...state,
  transferInfo: {
    ...state?.transferInfo,
    infoPax: {
      ...state?.transferInfo?.infoPax,
      listOfPax:
        state?.transferInfo?.infoPax?.listOfPax?.map((pax) => ({
          ...pax,
          campi1a10Found: {
            ...pax?.campi1a10Found
          }
        })) || []
    }
  }
})

/**
 * Verifica se lo stato contiene dati di form già compilati (trasferta o servizi).
 * @param {Object} [state] - Stato Gigi
 * @returns {boolean} True se c'è contenuto utile per il form
 */
export const hasMeaningfulFormState = (state) => {
  const transfer = state?.transferInfo?.requestDataTransfer
  if (
    transfer?.departureDate ||
    transfer?.arrivalDate ||
    transfer?.arrivalPlace?.trim() ||
    transfer?.departurePlace?.trim()
  ) {
    return true
  }
  const services = state?.transferInfo?.infoPax?.listOfPax?.[0]?.infoServices
  return ['plane', 'train', 'hotel', 'car'].some((key) => services?.[key]?.length > 0)
}

/**
 * Il messaggio dell'ultimo errore di unit registrato nello stato del processo, senza stack.
 * È l'errore vero dietro un `errorCode` generico: «manca la configurazione IMAP», «sessione
 * Goelett non creata», … (`state.ERROR`, riempito da `Process.executeUnit`).
 *
 * @param {Object} [payload] - Payload effettivo del form
 * @returns {string} Messaggio, o stringa vuota se non c'è
 */
export const getErroreProcesso = (payload) => {
  const errori = Array.isArray(payload?.ERROR) ? payload.ERROR : []
  const ultimo = [...errori].reverse().find((e) => typeof e?.message === 'string' && e.message)
  return ultimo ? ultimo.message.split('\n')[0].trim() : ''
}

/**
 * Il form segue le regole di obbligatorietà di Goelett? Sul Process-Unit lo dice il client
 * (`processLoadQuotes`), sull'agentico il contratto della conversazione, riportato da
 * `payloadDaRichiestaAgentica` in `formProfile`.
 *
 * Su Goelett è obbligatorio **solo** ciò che serve alla Search (docs/nora, catalogo
 * `goelettRequiredFields`): date e luoghi del servizio, il ritorno quando è andata e ritorno.
 * Orari, riconsegna dell'auto, classi, stazioni e i dati della trasferta MidOffice non
 * bloccano niente.
 *
 * @param {Object} [state] - Stato del form
 * @returns {boolean}
 */
export const isGoelettForm = (state) =>
  state?.processLoadQuotes === 'goelettQuotes' || state?.formProfile === 'goelett'

/** Campi che su Goelett non sono obbligatori, anche se lo sono per il MidOffice. */
export const GOELETT_OPTIONAL_FIELD_IDS = new Set([
  // trasferta MidOffice: Goelett non crea pratiche
  'departureDate',
  'arrivalDate',
  'departurePlace',
  'arrivalPlace',
  // auto: round-trip, la riconsegna riusa il ritiro; orari e categorie sono preferenze
  'pickupHour',
  'dropoffHour',
  'dropoffPlace',
  'carCategory',
  'carType',
  'carTransmission',
  // treno: orari e stazioni non sono vincoli della Search
  'fromHourDeparture',
  'fromHourReturn',
  'trainStationDepartureDyn',
  'trainStationArrivalDyn',
  'trainStationDeparture',
  'trainStationArrival'
])

/**
 * Il campo è facoltativo nello stato dato? Unisce il flag del campo e il profilo Goelett.
 * Le date dei servizi hanno gli stessi id della trasferta (`departureDate`, `arrivalDate`
 * del treno e dell'aereo): per quelle vale la regola del servizio, non l'elenco sopra.
 *
 * @param {Object} state - Stato del form
 * @param {Object} item - Definizione del campo
 * @returns {boolean}
 */
export const isFieldNotRequired = (state, item) => {
  if (item?.notRequired) return true
  if (!isGoelettForm(state) || !GOELETT_OPTIONAL_FIELD_IDS.has(item?.id)) return false
  const servizio = item?.service
  if (servizio === 'plane' || servizio === 'train') {
    return !['departureDate', 'arrivalDate'].includes(item.id)
  }
  return true
}

const DATA_GG_MM_AAAA = /^(\d{2})\/(\d{2})\/(\d{4})$/
const dataComeNumero = (valore) => {
  const m = DATA_GG_MM_AAAA.exec(valore || '')
  return m ? Date.UTC(Number(m[3]), Number(m[2]) - 1, Number(m[1])) : null
}
const primoValore = (...valori) => valori.find((v) => typeof v === 'string' && v.trim()) || ''

/**
 * Completa la trasferta con ciò che i servizi già dicono: partenza la prima data, fine
 * l'ultima, destinazione dal primo servizio che la nomina. Non tocca i campi già presenti:
 * ciò che il viaggiatore ha detto esplicitamente vale più di una deduzione.
 *
 * Il form dell'operatore nasce sullo stato del Process-Unit, dove queste informazioni sono
 * nella trasferta; sulla richiesta agentica stanno solo nei servizi, e la sezione resterebbe
 * vuota e in rosso anche con tutti i dati a schermo poco sotto.
 *
 * @param {Object} [transfer] - `richiesta.transfer`, se c'è
 * @param {Object} infoServices - Servizi della richiesta per tipo
 * @returns {{transfer: Object, derivati: Object}} Trasferta completata e, a parte, i soli
 *   campi dedotti: servono a non riscriverli nella richiesta se l'operatore non li tocca
 *   («salvare senza toccare nulla non cambia niente», vedi `requestFromForm` lato API)
 */
export const completaTrasfertaDaiServizi = (transfer, infoServices) => {
  const base = { ...(transfer || {}) }
  const { plane = [], train = [], hotel = [], car = [] } = infoServices || {}
  const inizi = [
    ...plane.map((x) => x?.departureDate),
    ...train.map((x) => x?.departureDate),
    ...hotel.map((x) => x?.checkInDate),
    ...car.map((x) => x?.pickupDate)
  ].filter((d) => dataComeNumero(d) !== null)
  const fini = [
    ...plane.map((x) => x?.arrivalDate),
    ...train.map((x) => x?.arrivalDate),
    ...hotel.map((x) => x?.checkOutDate),
    ...car.map((x) => x?.dropoffDate)
  ].filter((d) => dataComeNumero(d) !== null)

  const ordina = (date, verso) =>
    [...date].sort((a, b) => verso * (dataComeNumero(a) - dataComeNumero(b)))[0]

  if (!base.departureDate && inizi.length) base.departureDate = ordina(inizi, 1)
  if (!base.arrivalDate) {
    const ultime = fini.length ? fini : inizi
    if (ultime.length) base.arrivalDate = ordina(ultime, -1)
  }
  if (!base.departurePlace) {
    base.departurePlace = primoValore(
      plane[0]?.airportDepartureDescription,
      train[0]?.trainCityDeparture
    )
  }
  if (!base.arrivalPlace) {
    base.arrivalPlace = primoValore(
      plane[0]?.airportArrivalDescription,
      train[0]?.trainCityArrival,
      hotel[0]?.hotelCity,
      car[0]?.pickupPlace
    )
  }
  for (const campo of ['departurePlace', 'arrivalPlace']) if (!base[campo]) delete base[campo]

  const derivati = {}
  for (const [campo, valore] of Object.entries(base)) {
    if (valore !== (transfer || {})[campo]) derivati[campo] = valore
  }
  return { transfer: base, derivati }
}

/**
 * Restituisce il payload stato da applicare al form, allineato a getConversationOperatore.
 *
 * Per conversazioni agentiche (`executionEngine === 'agent'`) usa la richiesta
 * normalizzata. Per il Process-Unit preferisce finalState dell'ultimo processo e, se
 * assente, quello del processo precedente.
 *
 * «Assente» non basta come condizione. Un processo tecnico — le due esecuzioni di booking
 * Goelett (`goelettBookingFlow` e `goelettCompleteBookingFlow`, avviate dalla pagina
 * preventivo), l'escalation, il briefing — chiude con un finalState non vuoto ma privo di
 * `transferInfo`, perché nella sua catena non girano le unit che lo popolano. Preso così,
 * il form si svuoterebbe dei valori appena il viaggiatore accetta il preventivo.
 *
 * Il payload si compone quindi a due strati: base il finalState più recente che ha dati di
 * form, sopra i campi dell'ultimo processo, che porta lo stato corrente della conversazione
 * (`allDataElaborated`, `gigiFormEditable`, esito del booking) che Form legge subito dopo.
 * I processi di booking non hanno affatto la chiave `transferInfo`, quindi il merge shallow
 * non la sovrascrive con una versione parziale.
 *
 * @param {Object} conversation - Conversazione con processes e lastProcessStep
 * @param {Object} [lastProcessStep] - Ultimo process step della conversazione
 * @returns {Object} Payload initialState/finalState da normalizzare con getStateDefaultData
 */
/**
 * Traduce la richiesta del motore agentico nella forma che il form conosce.
 *
 * Il form dell'operatore è nato sullo state del motore legacy: `transferInfo`,
 * `infoPax.listOfPax[0].infoServices`. La richiesta agentica ha gli stessi nomi di campo —
 * il catalogo è generato dagli stessi JSON — ma una struttura più piatta, perché non è lo
 * state di un processo: è la richiesta e basta. Qui si rimette nella forma attesa.
 *
 * @param {Object} agentRequest - `{ json, revision }` come lo restituisce BusinessAI
 * @param {Object} [traveller] - Viaggiatore della conversazione
 * @returns {Object|null} Payload nella forma legacy, o null se non c'è richiesta
 */
/**
 * D47 — dal vocabolario della richiesta agentica (`Economy`, `Manual`, `Breakfast`) ai codici
 * del form, che è nato sul Process-Unit e parla ACRISS. L'inverso è `istanzaDaForm` lato API
 * (`services/info/functions/create/vocabolariForm.js`): le due tabelle vanno tenute insieme.
 */
export const CODICE_FORM_DA_VOCABOLARIO = {
  car: {
    carCategory: {
      Mini: 'M',
      Economy: 'E',
      Compact: 'C',
      Intermediate: 'I',
      Standard: 'S',
      FullSize: 'F',
      Luxury: 'L',
      Premium: 'P'
    },
    carType: {
      Door45: 'D',
      SUV: 'F',
      PassengerVan: 'V',
      WagonEstate: 'W',
      Sport: 'S',
      Crossover: 'X',
      Monospace: 'N'
    },
    carTransmission: { Manual: 'M', Auto: 'A' },
    carEngineType: { Diesel: 'D', Hybrid: 'H', Electric: 'E', Petrol: 'P', LPG: 'L' }
  },
  hotel: {
    treatment: {
      RoomOnly: 'RO',
      Breakfast: 'BB',
      HalfBoard: 'HB',
      FullBoard: 'FB',
      AllInclusive: 'AL'
    }
  }
}

/**
 * Un servizio della richiesta agentica con i codici del form al posto del vocabolario.
 * Un valore che non è del vocabolario resta com'è: il form lo mostra e l'operatore decide.
 *
 * @param {string} servizio
 * @param {Object} istanza
 * @returns {Object}
 */
export const istanzaPerIlForm = (servizio, istanza) => {
  const tabelle = CODICE_FORM_DA_VOCABOLARIO[servizio]
  if (!tabelle || !istanza || typeof istanza !== 'object') return istanza
  const copia = { ...istanza }
  for (const [campo, tabella] of Object.entries(tabelle)) {
    const codice = tabella[copia[campo]]
    if (codice) copia[campo] = codice
  }
  return copia
}

const placeText = (valore) => (typeof valore === 'string' && valore.trim() ? valore.trim() : '')

/**
 * La stazione come la disegna il form MidOffice: un oggetto CMS con `stationName`.
 * La richiesta agentica, quando nomina la stazione, ha il testo in
 * `trainStationDeparture` / `trainStationArrival`. Senza questa proiezione il campo
 * `trainStationDepartureDyn` resta vuoto anche con «Milano Centrale» già salvato.
 *
 * È solo vista. `requestFromForm` toglie l'oggetto prima di riscrivere la richiesta:
 * il modello non conosce `*Dyn` e il turno dopo lo perderebbe.
 *
 * @param {Object} istanza
 * @returns {Object}
 */
export const stationForForm = (istanza) => {
  if (!istanza || typeof istanza !== 'object') return istanza
  const copia = { ...istanza }
  for (const [textField, objectField] of [
    ['trainStationDeparture', 'trainStationDepartureDyn'],
    ['trainStationArrival', 'trainStationArrivalDyn']
  ]) {
    const nome = placeText(copia[textField])
    const giaOggetto =
      copia[objectField] &&
      typeof copia[objectField] === 'object' &&
      placeText(copia[objectField].stationName)
    if (!nome || giaOggetto) continue
    copia[objectField] = {
      stationName: nome,
      ...(placeText(copia[`${textField}Code`])
        ? { stationCode: placeText(copia[`${textField}Code`]) }
        : {})
    }
  }
  return copia
}

/**
 * Il luogo del volo quando lo IATA non c'è ancora.
 *
 * Su Goelett la ricerca parte sulla città e non scrive `airportDeparture`: il form,
 * che mostra solo il codice, restava vuoto con Roma e Milano già nella trasferta o
 * nella tratta (`legDeparturePlace`). La descrizione riempie il campo in lettura.
 * Non è uno IATA: salvarla come codice restringerebbe la ricerca a uno scalo che
 * nessuno ha scelto, quindi `requestFromForm` la toglie se il codice è ancora assente.
 *
 * @param {Object} istanza
 * @param {Object} [transfer]
 * @returns {Object}
 */
export const airportForForm = (istanza, transfer) => {
  if (!istanza || typeof istanza !== 'object') return istanza
  const copia = { ...istanza }
  const coppie = [
    ['airportDeparture', 'airportDepartureDescription', 'legDeparturePlace', 'departurePlace'],
    ['airportArrival', 'airportArrivalDescription', 'legArrivalPlace', 'arrivalPlace']
  ]
  for (const [codice, descrizione, tratta, trasferta] of coppie) {
    if (placeText(copia[codice]) || placeText(copia[descrizione])) continue
    const luogo = placeText(copia[tratta]) || placeText(transfer?.[trasferta])
    if (luogo) copia[descrizione] = luogo
  }
  return copia
}

export const payloadDaRichiestaAgentica = (agentRequest, traveller, conversation) => {
  const richiesta = agentRequest?.json
  if (!richiesta || typeof richiesta !== 'object') return null

  const infoServices = {}
  for (const servizio of ['plane', 'train', 'hotel', 'car']) {
    if (Array.isArray(richiesta[servizio]) && richiesta[servizio].length) {
      infoServices[servizio] = richiesta[servizio].map((istanza) => {
        const tradotta = istanzaPerIlForm(servizio, istanza)
        if (servizio === 'plane') return airportForForm(tradotta, richiesta.transfer)
        if (servizio === 'train') return stationForForm(tradotta)
        return tradotta
      })
    }
  }

  // Il blocco "Dati viaggiatore" del form legge il profilo WS02, che il percorso
  // agentico interroga al momento della ricerca e non conserva. Quello che sappiamo
  // comunque — chi è il viaggiatore — va messo lì: un pannello vuoto fa sembrare che
  // la conversazione non abbia un intestatario. Il codice cliente non è disponibile nel
  // DTO agentico e la sezione resta dichiaratamente bloccata nel form.
  const profileData = traveller
    ? {
        profiloViaggiatoreWS02: {
          keyProfilo: { personalID: traveller.pid || '' },
          datiPersonali: { nome: traveller.name || '', cognome: traveller.surname || '' },
          recapitiProfessionali: { eMail: traveller.email || '' }
        }
      }
    : {}

  // La sezione «Dati viaggiatore» senza MidOffice (Goelett, Demo) legge questi quattro campi
  // piatti e non `profileData`: sulla richiesta agentica restavano vuoti con l'intestatario
  // già noto.
  // Nome e cognome possono mancare in anagrafica (account di prova): senza un ripiego
  // la sezione esce bianca anche con l'email già nota. L'email non è un nome, ma è
  // l'unico intestatario che la conversazione ha.
  const nomeViaggiatore = traveller?.name || ''
  const cognomeViaggiatore = traveller?.surname || ''
  const datiViaggiatore = traveller
    ? {
        idAccount: traveller.pid || '',
        name: nomeViaggiatore || (!cognomeViaggiatore ? traveller.email || '' : ''),
        surname: cognomeViaggiatore,
        email: traveller.email || ''
      }
    : {}

  const { transfer, derivati } = completaTrasfertaDaiServizi(richiesta.transfer, infoServices)

  return {
    transferInfo: {
      requestDataTransfer: transfer,
      infoPax: { numPax: 1, listOfPax: [{ infoServices, profileData, ...datiViaggiatore }] }
    },
    // I campi della trasferta dedotti dai servizi: se al salvataggio sono invariati non
    // entrano nella richiesta (`requestFromForm`).
    transferDerived: derivati,
    // Il contratto della conversazione decide quali campi sono obbligatori (vedi `isGoelettForm`).
    ...(conversation?.scenario?.integrationId === 'goelett' ? { formProfile: 'goelett' } : {}),
    allDataElaborated: true,
    gigiFormEditable: conversation?.status === 'paused',
    engine: 'agent',
    agentRequestRevision: agentRequest.revision
  }
}

export const getEffectiveServerPayload = (conversation, lastProcessStep) => {
  // La richiesta agentica è la fonte canonica del form: un processo residuo può
  // contenere uno snapshot legacy non rappresentativo della richiesta corrente.
  if (conversation?.executionEngine === 'agent') {
    const agentico = payloadDaRichiestaAgentica(
      conversation?.agentRequest,
      conversation?.traveller,
      conversation
    )
    if (agentico) return agentico
  }

  const processes = [...(conversation?.processes || [])].sort((a, b) => b.id - a.id)
  const lastProcess = processes[0]
  const payload = lastProcessStep?.initialState || {}
  const hasState = (process) => !!process?.finalState && Object.keys(process.finalState).length > 0

  if (hasState(lastProcess)) {
    if (hasMeaningfulFormState(lastProcess.finalState)) return lastProcess.finalState

    const lastMeaningful = processes.find(
      (p) => p.id !== lastProcess.id && hasMeaningfulFormState(p?.finalState)
    )
    if (!lastMeaningful) return lastProcess.finalState

    return { ...lastMeaningful.finalState, ...lastProcess.finalState }
  }

  const previousWithState = processes.find((p) => p.id !== lastProcess?.id && hasState(p))
  if (previousWithState) return previousWithState.finalState

  return payload
}

/**
 * Indica se l'operatore può prendere in carico la conversazione (stessa logica di Form).
 * @param {Object} conversation - Conversazione
 * @param {number} lastProcessState - Stato numerico dell'ultimo processo
 * @param {boolean} [canTakeChargeEnv=false] - Flag environment.canTakeCharge
 * @returns {boolean} True se il pulsante "Prendi in carico" è disponibile
 */
export const isTakeChargeAvailable = (conversation, lastProcessState, canTakeChargeEnv = false) => {
  const { RUNNING, READYTORESTART } = { RUNNING: 2, READYTORESTART: 7 }
  return (
    conversation?.executionEngine !== 'agent' &&
    conversation?.status !== 'handledByOperator' &&
    conversation?.status !== 'ended' &&
    conversation?.status !== 'ignored' &&
    conversation?.status !== 'canceled' &&
    conversation?.status !== 'error' &&
    lastProcessState !== RUNNING &&
    lastProcessState !== READYTORESTART &&
    !!canTakeChargeEnv
  )
}

export const transferFields = [
  {
    label: { en: 'Departure date', it: 'Data di partenza' },
    location: 'transferInfo.requestDataTransfer.departureDate',
    id: 'departureDate',
    type: 'date',
    minDate: new Date()
  },
  {
    label: { en: 'Return date', it: 'Data di ritorno' },
    location: 'transferInfo.requestDataTransfer.arrivalDate',
    id: 'arrivalDate',
    type: 'date',
    minDate: new Date()
  },
  {
    label: { en: 'Place of departure', it: 'Luogo di partenza' },
    location: 'transferInfo.requestDataTransfer.departurePlace',
    id: 'departurePlace'
  },
  {
    label: { en: 'Place of destination', it: 'Luogo di destinazione' },
    location: 'transferInfo.requestDataTransfer.arrivalPlace',
    id: 'arrivalPlace'
  },
  {
    label: { en: 'Number of passengers', it: 'Numero passeggeri' },
    location: 'transferInfo.infoPax.numPax',
    id: 'numPax',
    type: 'numeric',
    notRequired: true
  }
]

export const travellerFields = [
  {
    label: { it: 'PID', en: 'PID' },
    location:
      'transferInfo.infoPax.listOfPax.0.profileData.profiloViaggiatoreWS02.keyProfilo.personalID',
    id: 'pid'
  },
  {
    label: { en: 'Name', it: 'Nome' },
    location:
      'transferInfo.infoPax.listOfPax.0.profileData.profiloViaggiatoreWS02.datiPersonali.nome',
    id: 'name'
  },
  {
    label: { en: 'Surname', it: 'Cognome' },
    location:
      'transferInfo.infoPax.listOfPax.0.profileData.profiloViaggiatoreWS02.datiPersonali.cognome',
    id: 'lastname'
  },
  {
    label: { en: 'Email', it: 'Email' },
    location:
      'transferInfo.infoPax.listOfPax.0.profileData.profiloViaggiatoreWS02.recapitiProfessionali.eMail',
    id: 'email'
  },
  {
    label: { en: 'Code client', it: 'Codice Cliente' },
    location:
      'transferInfo.infoPax.listOfPax.0.profileData.profiloViaggiatoreWS02.datiCliente.codiceCliente',
    id: 'clientCode',
    type: 'clientCode'
  },
  {
    label: { en: 'Customer company name', it: 'Ragione Sociale Cliente' },
    location:
      'transferInfo.infoPax.listOfPax.0.profileData.profiloViaggiatoreWS02.datiCliente.ragioneSocLunga',
    id: 'clientBusinessName',
    type: 'clientBusinessName'
  },
  {
    label: { en: 'Client city', it: 'Città Cliente' },
    location:
      'transferInfo.infoPax.listOfPax.0.profileData.profiloViaggiatoreWS02.datiCliente.localita',
    id: 'clientCity',
    notRequired: true
  },
  {
    label: { en: 'Client Address', it: 'Indirizzo Cliente' },
    location:
      'transferInfo.infoPax.listOfPax.0.profileData.profiloViaggiatoreWS02.datiCliente.indirizzo',
    id: 'clientStreet',
    notRequired: true
  }
]

export const genericTravellerFields = [
  {
    label: { en: 'Id Account', it: 'Id Account' },
    location: 'transferInfo.infoPax.listOfPax.0.idAccount',
    id: 'idAccount'
  },
  {
    label: { en: 'Name', it: 'Nome' },
    location: 'transferInfo.infoPax.listOfPax.0.name',
    id: 'name',
    notRequired: true
  },
  {
    label: { en: 'Surname', it: 'Cognome' },
    location: 'transferInfo.infoPax.listOfPax.0.surname',
    id: 'lastname',
    notRequired: true
  },
  {
    label: { en: 'Email', it: 'Email' },
    location: 'transferInfo.infoPax.listOfPax.0.email',
    id: 'email'
  }
]

export const serviceFields = [
  {
    id: 'typeTrip',
    label: {
      it: 'Tipo di viaggio',
      en: 'Trip Type'
    },
    service: 'train',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.train.#COUNT.typeTrip',
    type: 'dropdown',
    options: [
      { label: t.gfSolaAndata, value: 'OW' },
      { value: 'RT', label: t.gfAndataRitorno }
    ],
    order: 1
  },
  {
    id: 'companyName',
    label: { it: 'Nome della compagnia', en: 'Company Name' },
    service: 'train',
    notRequired: true,
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.train.#COUNT.companyName',
    order: 2
  },
  {
    id: 'classType',
    notRequired: true,
    label: { it: 'Tipo di classe', en: 'Class Type' },
    service: 'train',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.train.#COUNT.classType',
    order: 3
  },
  {
    id: 'seatPreference',
    label: { it: 'Preferenza posto', en: 'Seat Preference' },
    notRequired: true,
    service: 'train',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.train.#COUNT.seatPreference',
    order: 4
  },
  {
    id: 'departureDate',
    label: { it: 'Data di partenza', en: 'Departure Date' },
    service: 'train',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.train.#COUNT.departureDate',
    type: 'date',
    minDate: new Date(),
    order: 5
  },
  {
    id: 'trainCityDeparture',
    label: { it: 'Città di partenza', en: 'Train Departure City' },
    service: 'train',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.train.#COUNT.trainCityDeparture',
    order: 6
  },
  {
    id: 'fromHourDeparture',
    label: { it: 'Ora di partenza andata', en: 'Departure Time' },
    service: 'train',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.train.#COUNT.fromHourDeparture',
    type: 'time',
    order: 7
  },
  {
    id: 'toHourDeparture',
    label: { it: 'Ora di arrivo andata', en: 'Arrival Time Departure' },
    notRequired: true,
    service: 'train',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.train.#COUNT.toHourDeparture',
    type: 'time',
    order: 8
  },
  {
    id: 'arrivalDate',
    label: { it: 'Data di ritorno', en: 'Arrival Date' },
    service: 'train',
    notRequired: true,
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.train.#COUNT.arrivalDate',
    type: 'date',
    minDate: new Date(),
    order: 9
  },
  {
    id: 'trainCityArrival',
    label: { it: 'Città di arrivo', en: 'Train Arrival City' },
    service: 'train',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.train.#COUNT.trainCityArrival',
    order: 10
  },
  {
    id: 'fromHourReturn',
    label: { it: 'Ora di partenza ritorno', en: 'Return Departure Time' },
    service: 'train',
    notRequired: true,
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.train.#COUNT.fromHourReturn',
    type: 'time',
    order: 11
  },
  {
    id: 'toHourReturn',
    label: { it: 'Ora di arrivo ritorno', en: 'Return Arrival Time' },
    notRequired: true,
    service: 'train',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.train.#COUNT.toHourReturn',
    type: 'time',
    order: 12
  },
  {
    id: 'trainStationDepartureDyn',
    label: { it: 'Stazione di partenza', en: 'Train Departure Station' },
    type: 'station',
    service: 'train',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.train.#COUNT.trainStationDepartureDyn',
    order: 13
  },
  {
    id: 'trainStationDeparture',
    label: { it: 'Stazione di partenza', en: 'Train Departure Station' },
    type: 'station',
    service: 'train',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.train.#COUNT.trainStationDeparture',
    order: 14
  },
  {
    id: 'trainStationArrivalDyn',
    label: { it: 'Stazione di arrivo', en: 'Train Arrival Station' },
    type: 'station',
    service: 'train',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.train.#COUNT.trainStationArrivalDyn',
    order: 15
  },
  {
    id: 'trainStationArrival',
    label: { it: 'Stazione di arrivo', en: 'Train Arrival Station' },
    type: 'station',
    service: 'train',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.train.#COUNT.trainStationArrival',
    order: 16
  },
  {
    id: 'withinHoursDeparture',
    label: { it: 'Arrivo entro le ore andata', en: 'Within hours departure' },
    service: 'train',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.train.#COUNT.withinHoursDeparture',
    order: 17
  },
  {
    id: 'withinHoursArrival',
    label: { it: 'Arrivo entro le ore ritorno', en: 'Within hours arrival' },
    service: 'train',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.train.#COUNT.withinHoursArrival',
    order: 18
  },
  {
    id: 'carnet',
    label: { it: 'Carnet', en: 'Carnet' },
    service: 'train',
    notRequired: true,
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.train.#COUNT.carnet',
    order: 19
  },
  {
    id: 'noteAggiuntive',
    label: { it: 'Note servizio', en: 'Service Note' },
    type: 'textarea',
    service: 'train',
    notRequired: true,
    fullLine: true,
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.train.#COUNT.noteAggiuntive',
    order: 20
  },
  {
    id: 'checkInDate',
    label: { it: 'Data di check-in', en: 'Check-In Date' },
    service: 'hotel',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.hotel.#COUNT.checkInDate',
    type: 'date',
    minDate: new Date(),
    order: 1
  },
  {
    id: 'checkOutDate',
    label: { it: 'Data di check-out', en: 'Check-Out Date' },
    service: 'hotel',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.hotel.#COUNT.checkOutDate',
    type: 'date',
    minDate: new Date(),
    order: 2
  },
  {
    id: 'hotelCity',
    label: { it: 'Città o zona hotel', en: 'Hotel city or zone' },
    service: 'hotel',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.hotel.#COUNT.hotelCity',
    order: 3
  },
  {
    id: 'hotelName',
    label: { it: 'Nome hotel', en: 'Hotel Name' },
    service: 'hotel',
    notRequired: true,
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.hotel.#COUNT.hotelName',
    order: 5
  },
  {
    id: 'hotelStars',
    label: { it: 'Stelle hotel', en: 'Hotel Stars' },
    service: 'hotel',
    notRequired: true,
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.hotel.#COUNT.hotelStars',
    order: 6
  },
  {
    id: 'hotelParking',
    label: { it: 'Parcheggio hotel', en: 'Hotel Parking' },
    service: 'hotel',
    type: 'dropdown',
    options: [
      { label: t.gfSi, value: true },
      { label: t.gfNo, value: false }
    ],
    notRequired: true,
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.hotel.#COUNT.hotelParking',
    order: 7
  },
  {
    id: 'treatment',
    label: { it: 'Trattamento', en: 'Treatment' },
    service: 'hotel',
    type: 'dropdown',
    options: [
      { value: ' ', label: t.gfNonSpecificato },
      { value: 'RO', label: t.gfSoloStanza },
      { value: 'BB', label: t.gfBedAndBreakfast },
      { value: 'HB', label: t.gfMezzaPensione },
      { value: 'FB', label: t.gfPensioneCompleta },
      { value: 'DN', label: t.gfCena },
      { value: 'LN', label: t.gfPranzo },
      { value: 'AL', label: t.gfAllInclusive }
    ],
    notRequired: true,
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.hotel.#COUNT.treatment',
    order: 8
  },
  {
    id: 'noteAggiuntive',
    label: { it: 'Note servizio', en: 'Service Note' },
    type: 'textarea',
    service: 'hotel',
    notRequired: true,
    fullLine: true,
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.hotel.#COUNT.noteAggiuntive',
    order: 9
  },
  {
    id: 'typeTrip',
    label: { it: 'Tipo di viaggio', en: 'Trip Type' },
    service: 'plane',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.plane.#COUNT.typeTrip',
    type: 'dropdown',
    options: [
      { label: t.gfSolaAndata, value: 'OW' },
      { value: 'RT', label: t.gfAndataRitorno }
    ],
    order: 1
  },
  {
    id: 'companyName',
    label: { it: 'Nome della compagnia', en: 'Company Name' },
    service: 'plane',
    notRequired: true,
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.plane.#COUNT.companyName',
    order: 2
  },
  {
    id: 'classType',
    label: { it: 'Tipo di classe', en: 'Class Type' },
    service: 'plane',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.plane.#COUNT.classType',
    notRequired: true,
    order: 3
  },
  {
    id: 'seatPreference',
    label: { it: 'Preferenza posto', en: 'Seat Preference' },
    notRequired: true,
    service: 'plane',
    type: 'dropdown',
    options: [
      { value: 'W', label: t.gfFinestrino },
      { value: 'A', label: t.gfCorridoio },
      { value: 'N', label: t.gfCentrale },
      { value: ' ', label: t.gfNonSpecificato }
    ],
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.plane.#COUNT.seatPreference',
    order: 4
  },
  {
    id: 'departureDate',
    label: { it: 'Data di partenza', en: 'Departure Date' },
    service: 'plane',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.plane.#COUNT.departureDate',
    type: 'date',
    minDate: new Date(),
    order: 5
  },
  {
    id: 'airportDeparture',
    label: {
      value: { it: 'Aeroporto di partenza', en: 'Departure Airport' },
      label: { it: 'Descrizione aeroporto di partenza', en: 'Departure Airport Description' }
    },
    type: 'airport',
    options: airports,
    service: 'plane',
    location: {
      value: 'transferInfo.infoPax.listOfPax.0.infoServices.plane.#COUNT.airportDeparture',
      label:
        'transferInfo.infoPax.listOfPax.0.infoServices.plane.#COUNT.airportDepartureDescription'
    },
    order: 6
  },
  {
    id: 'fromHourDeparture',
    label: { it: 'Ora di partenza andata', en: 'Departure Time Departure' },
    service: 'plane',
    notRequired: true,
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.plane.#COUNT.fromHourDeparture',
    type: 'time',
    order: 7
  },
  {
    id: 'toHourDeparture',
    label: { it: 'Ora di arrivo andata', en: 'Arrival Time Departure' },
    notRequired: true,
    service: 'plane',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.plane.#COUNT.toHourDeparture',
    type: 'time',
    order: 8
  },
  {
    id: 'arrivalDate',
    label: { it: 'Data di ritorno', en: 'Arrival Date' },
    notRequired: true,
    service: 'plane',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.plane.#COUNT.arrivalDate',
    type: 'date',
    minDate: new Date(),
    order: 9
  },
  {
    id: 'airportArrival',
    label: {
      value: { it: 'Aeroporto di arrivo', en: 'Arrival Airport' },
      label: { it: 'Descrizione aeroporto di arrivo', en: 'Arrival Airport Description' }
    },
    type: 'airport',
    options: airports,
    service: 'plane',
    location: {
      value: 'transferInfo.infoPax.listOfPax.0.infoServices.plane.#COUNT.airportArrival',
      label: 'transferInfo.infoPax.listOfPax.0.infoServices.plane.#COUNT.airportArrivalDescription'
    },
    order: 10
  },
  {
    id: 'fromHourReturn',
    label: { it: 'Ora di partenza ritorno', en: 'Return Departure Time' },
    service: 'plane',
    notRequired: true,
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.plane.#COUNT.fromHourReturn',
    type: 'time',
    order: 11
  },
  {
    id: 'toHourReturn',
    label: { it: 'Ora di arrivo ritorno', en: 'Return Arrival Time' },
    notRequired: true,
    service: 'plane',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.plane.#COUNT.toHourReturn',
    type: 'time',
    order: 12
  },
  {
    id: 'luggageType',
    label: { it: 'Tipo di bagaglio', en: 'Luggage Type' },
    service: 'plane',
    notRequired: true,
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.plane.#COUNT.luggageType',
    order: 13
  },
  {
    id: 'luggageWeight',
    label: { it: 'Peso del bagaglio', en: 'Luggage Weight' },
    service: 'plane',
    notRequired: true,
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.plane.#COUNT.luggageWeight',
    order: 13
  },
  {
    id: 'luggageNumber',
    label: { it: 'Numero di bagagli', en: 'Luggage Number' },
    service: 'plane',
    notRequired: true,
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.plane.#COUNT.luggageNumber',
    order: 13
  },
  {
    id: 'typeBoarding',
    label: { it: 'Tipo di imbarco', en: 'Boarding Type' },
    service: 'plane',
    notRequired: true,
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.plane.#COUNT.typeBoarding',
    order: 14
  },
  {
    id: 'companyCode',
    label: { it: 'Codice compagnia', en: 'Company Code' },
    service: 'plane',
    notRequired: true,
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.plane.#COUNT.companyCode',
    order: 15
  },
  {
    id: 'noteAggiuntive',
    label: { it: 'Note servizio', en: 'Service Note' },
    type: 'textarea',
    service: 'plane',
    notRequired: true,
    fullLine: true,
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.plane.#COUNT.noteAggiuntive',
    order: 16
  },
  {
    id: 'pickupPlace',
    label: { it: 'Luogo di Ritiro', en: 'Pick-up Place' },
    service: 'car',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.car.#COUNT.pickupPlace',
    order: 1
  },
  {
    id: 'pickupDate',
    label: { it: 'Data di Ritiro', en: 'Pick-up Date' },
    service: 'car',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.car.#COUNT.pickupDate',
    type: 'date',
    minDate: new Date(),
    order: 2
  },
  {
    id: 'pickupHour',
    label: { it: 'Ora di Ritiro', en: 'Pick-up Hour' },
    service: 'car',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.car.#COUNT.pickupHour',
    type: 'time',
    order: 3
  },
  {
    id: 'dropoffPlace',
    label: { it: 'Luogo di Rilascio', en: 'Drop-off Place' },
    service: 'car',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.car.#COUNT.dropoffPlace',
    order: 4
  },
  {
    id: 'dropoffDate',
    label: { it: 'Data di Rilascio', en: 'Drop-off Date' },
    service: 'car',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.car.#COUNT.dropoffDate',
    type: 'date',
    minDate: new Date(),
    order: 5
  },
  {
    id: 'dropoffHour',
    label: { it: 'Ora di Rilascio', en: 'Drop-off Hour' },
    service: 'car',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.car.#COUNT.dropoffHour',
    type: 'time',
    order: 6
  },
  {
    id: 'carCategory',
    label: { it: 'Categoria auto', en: 'Car Category' },
    service: 'car',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.car.#COUNT.carCategory',
    type: 'dropdown',
    order: 7,
    options: [
      { value: '_', label: t.gfNonSpecificato },
      { value: 'M', label: t.gfMini },
      { value: 'E', label: t.gfEconomy },
      { value: 'C', label: t.gfCompact },
      { value: 'I', label: t.gfIntermediate },
      { value: 'S', label: t.gfStandard },
      { value: 'F', label: t.gfFullSize },
      { value: 'L', label: t.gfLuxury },
      { value: 'P', label: t.gfPremium }
    ]
  },
  {
    id: 'carType',
    label: { it: 'Tipo auto', en: 'Car Type' },
    service: 'car',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.car.#COUNT.carType',
    type: 'dropdown',
    order: 8,
    options: [
      { value: '_', label: t.gfNonSpecificato },
      { value: 'D', label: t.gf45Porte },
      { value: 'F', label: t.gfSuv },
      { value: 'V', label: t.gfVan }
    ],
    // Carrozzerie in più che sa gestire solo la ricerca Goelett: `options` resta il set
    // ACRISS che il Dinamico interpreta in `VehicleCode`, questi codici sono convenzioni
    // Nora↔Goelett (`X` qui è Crossover, in ACRISS è Special). Le usa `Sections/Car.jsx`
    // quando la conversazione gira sul flusso Goelett.
    goelettOptions: [
      { value: '_', label: t.gfNonSpecificato },
      { value: 'D', label: t.gf45Porte },
      { value: 'F', label: t.gfSuv },
      { value: 'V', label: t.gfVan },
      { value: 'W', label: t.gfStationWagon },
      { value: 'S', label: t.gfSportiva },
      { value: 'X', label: t.gfCrossover },
      { value: 'N', label: t.gfMonovolume }
    ]
  },
  {
    id: 'carTransmission',
    label: { it: 'Trasmissione auto', en: 'Car Transmission' },
    service: 'car',
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.car.#COUNT.carTransmission',
    type: 'dropdown',
    order: 9,
    options: [
      { value: '_', label: t.gfNonSpecificato },
      { value: 'M', label: t.gfManuale },
      { value: 'A', label: t.gfAutomatico }
    ]
  },
  {
    id: 'noteAggiuntive',
    label: { it: 'Note servizio', en: 'Service Note' },
    type: 'textarea',
    service: 'car',
    notRequired: true,
    fullLine: true,
    location: 'transferInfo.infoPax.listOfPax.0.infoServices.car.#COUNT.noteAggiuntive',
    order: 10
  }
]

/**
 * Campo alimentazione auto, esposto **solo** al flusso Goelett.
 *
 * Sta fuori da `serviceFields` di proposito: lì dentro finirebbe in `getElementsByService`
 * e in `addService`, che inizializza ogni campo del servizio e scriverebbe la chiave anche
 * sulle conversazioni Dinamico, dove `engineType` non ha un destinatario.
 */
export const goelettCarEngineTypeField = {
  id: 'carEngineType',
  label: { it: 'Alimentazione', en: 'Fuel type' },
  service: 'car',
  location: 'transferInfo.infoPax.listOfPax.0.infoServices.car.#COUNT.carEngineType',
  type: 'dropdown',
  notRequired: true,
  // nessun `order`: il campo lo rende direttamente `Sections/Car.jsx`, non passa da un
  // ordinamento come i campi di `serviceFields`.
  options: [
    { value: '_', label: t.gfNonSpecificato },
    { value: 'D', label: t.gfDiesel },
    { value: 'H', label: t.gfIbrida },
    { value: 'E', label: t.gfElettrica },
    { value: 'P', label: t.gfBenzina },
    { value: 'L', label: t.gfGpl }
  ]
}

export const getElementsByService = () => ({
  transfer: transferFields.reduce(
    (acc, { id, ...otherFields }) => ({ ...acc, [id]: { id, ...otherFields } }),
    {}
  ),
  traveller: travellerFields.reduce(
    (acc, { id, ...otherFields }) => ({ ...acc, [id]: { id, ...otherFields } }),
    {}
  ),
  genericTraveller: genericTravellerFields.reduce(
    (acc, { id, ...otherFields }) => ({ ...acc, [id]: { id, ...otherFields } }),
    {}
  ),
  train: serviceFields
    .filter(({ service }) => service === 'train')
    .reduce((acc, { id, ...otherFields }) => ({ ...acc, [id]: { id, ...otherFields } }), {}),
  hotel: serviceFields
    .filter(({ service }) => service === 'hotel')
    .reduce((acc, { id, ...otherFields }) => ({ ...acc, [id]: { id, ...otherFields } }), {}),
  plane: serviceFields
    .filter(({ service }) => service === 'plane')
    .reduce((acc, { id, ...otherFields }) => ({ ...acc, [id]: { id, ...otherFields } }), {}),
  car: serviceFields
    .filter(({ service }) => service === 'car')
    .reduce((acc, { id, ...otherFields }) => ({ ...acc, [id]: { id, ...otherFields } }), {})
})

export const getLabelMidofficeRef = (state) => {
  const {
    transferInfo: {
      infoPax: {
        listOfPax: [
          { transferMidOfficeRef: { annoTrasferta, numeroPratica, numeroTrasferta } = {} } = {}
        ] = []
      } = {}
    } = {}
  } = state
  if (!annoTrasferta || !numeroPratica || !numeroTrasferta) return ''
  return (
    '| Anno ' + annoTrasferta + ' - Pratica ' + numeroPratica + ' - Trasferta ' + numeroTrasferta
  )
}

export const getStateFromInfoServices = (infoServices) => ({
  transferInfo: { infoPax: { listOfPax: [{ infoServices }] } }
})

/**
 * Serializza un valore per il log delle modifiche (oldValue/newValue)
 * @param {*} val - Valore da serializzare
 * @returns {string}
 */
const serializeChangeValue = (val) => {
  if (val == null) return ''
  if (typeof val === 'object' && val !== null && 'stationName' in val) return val.stationName ?? ''
  if (typeof val === 'object') return JSON.stringify(val)
  return String(val)
}

/**
 * Considera vuoti undefined, null, stringa vuota e stringhe con solo spazi (evita falsi positivi)
 * @param {*} val
 * @returns {boolean}
 */
const isEmptyValue = (val) => {
  if (val == null) return true
  if (typeof val === 'string') return val.trim() === ''
  return false
}

/**
 * Restituisce le modifiche campo per campo tra stato originale e nuovo per il tracciamento operatore.
 * Usa getElementsByService e readKey; gestisce airport (value/label), station (stationName) e campi normali.
 * Per servizi multipli (plane, train, hotel, car) include serviceIndex e changeType in ogni elemento.
 * changeType: 'modified' = campo modificato, 'added' = servizio aggiunto, 'deleted' = servizio rimosso (una voce per ogni campo con newValue vuoto).
 *
 * @param {Object} originalState - Stato iniziale (es. stateOnDB)
 * @param {Object} newState - Stato dopo le modifiche (es. stateRef.current)
 * @returns {Object} Oggetto con chiavi transfer, traveller, plane, train, hotel, car, campi1a10 (array di { field, oldValue, newValue, serviceIndex?, changeType })
 */
export const getFormEditChanges = (originalState, newState) => {
  const elements = getElementsByService()
  const result = {
    transfer: [],
    traveller: [],
    plane: [],
    train: [],
    hotel: [],
    car: [],
    campi1a10: []
  }

  const getDiffWithValues = (correctState, state, service, i = false, changeType = 'modified') => {
    const arr = []
    const serviceIndex = i !== false ? i : undefined
    const fields = elements[service]
    if (!fields) return arr
    Object.keys(fields).forEach((key) => {
      const field = fields[key]
      const { id, location, type } = field
      let oldVal, newVal
      const loc = typeof location === 'string' ? location : location?.value
      if (type === 'airport') {
        oldVal = readKey(correctState, location.value, i)
        newVal = readKey(state, location.value, i)
        const oldLabel = readKey(correctState, location.label, i)
        const newLabel = readKey(state, location.label, i)
        if (oldVal === newVal && oldLabel === newLabel) return
        const oldStr = serializeChangeValue(oldLabel ?? oldVal)
        const newStr = serializeChangeValue(newLabel ?? newVal)
        if (isEmptyValue(oldStr) && isEmptyValue(newStr)) return
        arr.push({
          field: id,
          oldValue: oldStr,
          newValue: newStr,
          ...(serviceIndex !== undefined && { serviceIndex }),
          changeType
        })
        return
      }
      if (type === 'station') {
        const oldObj = readKey(correctState, loc, i)
        const newObj = readKey(state, loc, i)
        oldVal = oldObj?.stationName
        newVal = newObj?.stationName
      } else {
        oldVal = readKey(correctState, loc, i)
        newVal = readKey(state, loc, i)
      }
      if (oldVal === newVal) return
      const oldStr = serializeChangeValue(oldVal)
      const newStr = serializeChangeValue(newVal)
      if (isEmptyValue(oldStr) && isEmptyValue(newStr)) return
      arr.push({
        field: id,
        oldValue: oldStr,
        newValue: newStr,
        ...(serviceIndex !== undefined && { serviceIndex }),
        changeType
      })
    })
    return arr
  }

  /**
   * Per un servizio rimosso (indice i non più presente in newState), restituisce una voce per ogni campo
   * con changeType 'deleted', oldValue dallo stato originale e newValue vuoto.
   * @param {Object} state - Stato originale (per leggere i valori del servizio rimosso)
   * @param {string} service - Tipo servizio: 'plane' | 'train' | 'hotel' | 'car'
   * @param {number} i - Indice del servizio rimosso
   * @returns {Array<{ field, oldValue, newValue, serviceIndex, changeType }>}
   */
  const getRemovedServiceEntries = (state, service, i) => {
    const arr = []
    const fields = elements[service]
    if (!fields) return arr
    Object.keys(fields).forEach((key) => {
      const field = fields[key]
      const { id, location, type } = field
      const loc = typeof location === 'string' ? location : location?.value
      let oldVal
      if (type === 'airport') {
        const oldLabel = readKey(state, location?.label, i)
        oldVal = readKey(state, location?.value, i)
        oldVal = serializeChangeValue(oldLabel ?? oldVal)
      } else if (type === 'station') {
        const oldObj = readKey(state, loc, i)
        oldVal = serializeChangeValue(oldObj?.stationName)
      } else {
        oldVal = serializeChangeValue(readKey(state, loc, i))
      }
      arr.push({
        field: id,
        oldValue: oldVal ?? '',
        newValue: '',
        serviceIndex: i,
        changeType: 'deleted'
      })
    })
    return arr
  }

  result.transfer = getDiffWithValues(originalState, newState, 'transfer', false, 'modified')
  result.traveller = getDiffWithValues(originalState, newState, 'traveller', false, 'modified')

  const originalPlane =
    originalState?.transferInfo?.infoPax?.listOfPax?.[0]?.infoServices?.plane ?? []
  const newPlane = newState?.transferInfo?.infoPax?.listOfPax?.[0]?.infoServices?.plane ?? []
  const originalTrain =
    originalState?.transferInfo?.infoPax?.listOfPax?.[0]?.infoServices?.train ?? []
  const newTrain = newState?.transferInfo?.infoPax?.listOfPax?.[0]?.infoServices?.train ?? []
  const originalHotel =
    originalState?.transferInfo?.infoPax?.listOfPax?.[0]?.infoServices?.hotel ?? []
  const newHotel = newState?.transferInfo?.infoPax?.listOfPax?.[0]?.infoServices?.hotel ?? []
  const originalCar = originalState?.transferInfo?.infoPax?.listOfPax?.[0]?.infoServices?.car ?? []
  const newCar = newState?.transferInfo?.infoPax?.listOfPax?.[0]?.infoServices?.car ?? []

  newPlane.forEach((_, i) => {
    result.plane.push(
      ...getDiffWithValues(
        originalState,
        newState,
        'plane',
        i,
        i >= originalPlane.length ? 'added' : 'modified'
      )
    )
  })
  for (let i = newPlane.length; i < originalPlane.length; i++) {
    result.plane.push(...getRemovedServiceEntries(originalState, 'plane', i))
  }

  newTrain.forEach((_, i) => {
    result.train.push(
      ...getDiffWithValues(
        originalState,
        newState,
        'train',
        i,
        i >= originalTrain.length ? 'added' : 'modified'
      )
    )
  })
  for (let i = newTrain.length; i < originalTrain.length; i++) {
    result.train.push(...getRemovedServiceEntries(originalState, 'train', i))
  }

  newHotel.forEach((_, i) => {
    result.hotel.push(
      ...getDiffWithValues(
        originalState,
        newState,
        'hotel',
        i,
        i >= originalHotel.length ? 'added' : 'modified'
      )
    )
  })
  for (let i = newHotel.length; i < originalHotel.length; i++) {
    result.hotel.push(...getRemovedServiceEntries(originalState, 'hotel', i))
  }

  newCar.forEach((_, i) => {
    result.car.push(
      ...getDiffWithValues(
        originalState,
        newState,
        'car',
        i,
        i >= originalCar.length ? 'added' : 'modified'
      )
    )
  })
  for (let i = newCar.length; i < originalCar.length; i++) {
    result.car.push(...getRemovedServiceEntries(originalState, 'car', i))
  }

  if (newState?.environmentInfo?.enableCampi1a10) {
    const campi1a10Keys = new Set([
      ...(originalState?.transferInfo?.infoPax?.listOfPax?.[0]?.campi1a10 ?? [])
        .map((c) => c?.key)
        .filter(Boolean),
      ...(newState?.transferInfo?.infoPax?.listOfPax?.[0]?.campi1a10 ?? [])
        .map((c) => c?.key)
        .filter(Boolean)
    ])
    const basePath = 'transferInfo.infoPax.listOfPax.0.campi1a10Found'
    campi1a10Keys.forEach((key) => {
      const loc = `${basePath}.${key}`
      const oldVal = readKey(originalState, loc)
      const newVal = readKey(newState, loc)
      if (oldVal === newVal) return
      const oldStr = serializeChangeValue(oldVal)
      const newStr = serializeChangeValue(newVal)
      if (isEmptyValue(oldStr) && isEmptyValue(newStr)) return
      result.campi1a10.push({
        field: key,
        oldValue: oldStr,
        newValue: newStr,
        changeType: 'modified'
      })
    })
  }

  return result
}

/**
 * Genera un'etichetta per un log di Nora esclusa.
 *
 * <p>Questa funzione prende in input un oggetto logNora e restituisce una stringa
 * che descrive quando e da chi è stato esclusa il log. Se non ci sono dettagli sull'esclusione,
 * viene restituita una stringa vuota.</p>
 *
 * @param logNora L'oggetto contenente i dettagli del log di Nora esclusa.
 * @returns Una stringa che indica l'utente che ha esclusa Nora e il momento esatto dell'esclusione.
 *
 * <p>Autore: mranocchia</p>
 */
export const getLabelNoraLog = (logNora = {}) => {
  const { logNoraIsExcluded = {} } = logNora || {}
  const { atlanteId = '', username = '', date } = logNoraIsExcluded
  if (!atlanteId && !username) return ''
  const { day, month, year, hours, minutes, seconds } = formatDateSingleElement(date)
  return `Nora Esclusa da utente: ${(atlanteId || username || 'N/D').toUpperCase()} il ${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
}

/**
 * Genera un'etichetta per una conversazione cancellata.
 *
 * <p>Questa funzione prende in input un oggetto conversazione e restituisce una stringa
 * che descrive quando e da chi è stata cancellata l'email associata alla conversazione.
 * Se la cancellazione non è registrata, viene restituito un messaggio di default.</p>
 *
 * @param conversation L'oggetto conversazione contenente i dettagli della cancellazione.
 * @returns Una stringa che indica l'utente che ha cancellato l'email e il momento esatto della cancellazione.
 *
 * <p>Autore: mranocchia</p>
 */
export const getLabelConversationCanceled = (conversation) => {
  const { logDataConversation = {} } = conversation || {}
  const { logInfo = {} } = logDataConversation || {}
  const { conversation: conversationLog = {} } = logInfo || {}
  const { canceled = {} } = conversationLog || {}
  if (!canceled) return 'Email cancellata da middle office '
  const { date, idUtenteAdv } = canceled || {}
  const { day, month, year, hours, minutes, seconds } = formatDateSingleElement(date)
  return `Email cancellata da middle office da utente: ${(idUtenteAdv || 'N/D').toUpperCase()} il ${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
}

/**
 * Genera un'etichetta descrittiva per la creazione di un trasferimento in una conversazione.
 * Estrae le informazioni rilevanti dal log della conversazione e formatta una stringa con i dettagli del trasferimento.
 * Se i dati richiesti non sono disponibili o completi, restituisce 'Non definito'.
 *
 * @param conversation Oggetto della conversazione contenente i dati di log necessari.
 * @return Una stringa formattata che rappresenta l'etichetta del trasferimento creato, oppure 'Non definito' se mancano dati essenziali.
 *
 * Autore: mranocchia
 */
export const getLabelConversationCreateTransfer = (conversation) => {
  const { logDataConversation = {} } = conversation || {}
  const { logInfo = {} } = logDataConversation || {}
  const { conversation: conversationLog = {} } = logInfo || {}
  const { createTransfer = {} } = conversationLog || {}
  if (!createTransfer) return ''
  const { date, idUtenteAdv } = createTransfer || {}
  const { day, month, year, hours, minutes, seconds } = formatDateSingleElement(date)

  if (!idUtenteAdv) return 'Non definito.'
  if (!day || !month || !year || !hours || !minutes || !seconds) return 'Non definito.'
  return `${(idUtenteAdv || 'N/D').toUpperCase()} il ${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
}

/**
 * Formatta una data in singoli elementi.
 *
 * <p>Questa funzione prende una data in input e la scompone nei suoi componenti
 * principali: giorno, mese, anno, ore, minuti e secondi. Ogni componente è restituito
 * come stringa formattata con zeri iniziali dove necessario per garantire che ogni
 * elemento abbia sempre due cifre.</p>
 *
 * @param date La data da formattare.
 * @returns Un oggetto contenente i componenti della data: giorno, mese, anno, ore, minuti e secondi.
 *
 * <p>Autore: mranocchia</p>
 */
const formatDateSingleElement = (date) => {
  // get data in format DD/MM/YYYY HH:mm:ss
  const dataFormat = new Date(date)
  const day = dataFormat.getDate().toString().padStart(2, '0')
  const month = (dataFormat.getMonth() + 1).toString().padStart(2, '0')
  const year = dataFormat.getFullYear()
  const hours = dataFormat.getHours().toString().padStart(2, '0')
  const minutes = dataFormat.getMinutes().toString().padStart(2, '0')
  const seconds = dataFormat.getSeconds().toString().padStart(2, '0')
  return { day, month, year, hours, minutes, seconds }
}

/**
 * Aggiunge i nomi reali dopo i placeholder nel messaggio per lo sviluppo
 * Utilizza matchedEntities da stateRef se disponibile, altrimenti li ricostruisce da message e realMessageWithPPI
 * @param {string} message - Messaggio con placeholder tipo [PERSON_ID_001]
 * @param {string} realMessageWithPPI - Messaggio originale con nomi reali
 * @param {Object} stateRef - Riferimento allo state che può contenere matchedEntities
 * @returns {string} Messaggio con placeholder seguiti notedai nomi reali tra parentesi
 */
export const addRealNamesToPlaceholders = (message, realMessageWithPPI, stateRef = null) => {
  if (!message || !realMessageWithPPI) return message || ''

  // Trova tutti i placeholder nel formato [PERSON_ID_XXX]
  const placeholderRegex = /\[PERSON_ID_\d+\]/g
  const placeholders = message.match(placeholderRegex) || []

  if (placeholders.length === 0) return message

  let matchedEntities = []

  // Prova a ottenere matchedEntities da stateRef
  if (stateRef?.current?.matchedEntities) {
    matchedEntities = stateRef.current.matchedEntities
  } else if (stateRef?.matchedEntities) {
    matchedEntities = stateRef.matchedEntities
  } else {
    // Ricostruisci matchedEntities confrontando message e realMessageWithPPI
    // Trova tutti i placeholder unici nell'ordine in cui appaiono
    const uniquePlaceholders = [...new Set(placeholders)]

    // Ordina i placeholder per posizione nel message per mapparli nell'ordine corretto
    const placeholderWithPositions = uniquePlaceholders
      .map((placeholder) => {
        const position = message.indexOf(placeholder)
        return { placeholder, position }
      })
      .filter((p) => p.position !== -1)
      .sort((a, b) => a.position - b.position)

    // Per ogni placeholder, trova il nome corrispondente in ordine
    let lastMatchedIndex = 0
    placeholderWithPositions.forEach(({ placeholder, position }) => {
      // Se già mappato, salta
      if (matchedEntities.find((e) => e.id === placeholder)) return

      // Estrai il contesto prima del placeholder (ultimi 100 caratteri)
      const contextStart = Math.max(0, position - 100)
      const beforePlaceholder = message.substring(contextStart, position)

      // Cerca questo contesto nel realMessageWithPPI, partendo dall'ultima posizione matchata
      let beforeIndex = realMessageWithPPI.indexOf(beforePlaceholder, lastMatchedIndex)
      if (beforeIndex === -1) {
        // Se non trova il contesto esatto, prova a cercare un contesto più corto
        for (let i = Math.min(beforePlaceholder.length, 80); i >= 20; i -= 5) {
          const shorterContext = beforePlaceholder.substring(beforePlaceholder.length - i)
          beforeIndex = realMessageWithPPI.indexOf(shorterContext, lastMatchedIndex)
          if (beforeIndex !== -1) {
            beforeIndex += beforePlaceholder.length - shorterContext.length
            break
          }
        }
      }

      if (beforeIndex !== -1) {
        // Estrai il testo che corrisponde al placeholder
        const startPos = beforeIndex + beforePlaceholder.length
        const endPos = Math.min(realMessageWithPPI.length, startPos + 200)
        const candidateText = realMessageWithPPI.substring(startPos, endPos)

        // Trova il nome estraendo tutto fino al primo newline
        // Poi pulisci il nome rimuovendo caratteri non validi
        const newlineIndex = candidateText.indexOf('\n')
        if (newlineIndex !== -1) {
          const potentialName = candidateText.substring(0, newlineIndex).trim()
          // Verifica che sia un nome valido (solo lettere, spazi, apostrofi, trattini)
          // e che inizi con una lettera maiuscola
          if (
            potentialName.length >= 2 &&
            potentialName.length < 100 &&
            /^[A-ZÀ-Ÿ][A-Za-zÀ-ÿ '-]*$/.test(potentialName)
          ) {
            matchedEntities.push({
              id: placeholder,
              name: potentialName
            })
            // Aggiorna l'ultima posizione matchata per il prossimo placeholder
            lastMatchedIndex = startPos + newlineIndex + 1
          }
        }
      }
    })
  }

  // Crea una mappa placeholder -> nome reale
  const placeholderMap = new Map()
  matchedEntities.forEach((entity) => {
    if (entity.id && entity.name) {
      placeholderMap.set(entity.id, entity.name)
    }
  })

  // Sostituisci ogni placeholder con placeholder (nome reale)
  let result = message
  placeholderMap.forEach((realName, placeholder) => {
    // Usa String.replace con una stringa letterale invece di regex per evitare problemi di escape
    // Sostituisci tutte le occorrenze del placeholder
    const replacement = `${placeholder} (${realName})`
    // Split e join per sostituire tutte le occorrenze
    result = result.split(placeholder).join(replacement)
  })

  return result
}
